import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
} from "recharts";
import AdminLayout from "@/components/app/AdminLayout";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";

const FUNNEL_COLORS = ["#1c1917", "#44403c", "#d97706", "#10b981"];

export default function AdminAnalytics() {
  const { campaignId: routeCampaignId } = useParams();
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState(routeCampaignId || "");
  const [data, setData] = useState(null);
  const [sections, setSections] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/campaigns");
        setCampaigns(data);
        if (!campaignId && data.length > 0) {
          const def = data.find((c) => c.is_default) || data[0];
          setCampaignId(def.id);
        }
      } catch (err) {
        console.error("Failed to load campaigns:", err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!campaignId) return;
    (async () => {
      try {
        const [a, s] = await Promise.all([
          api.get(`/analytics/campaign/${campaignId}`),
          api.get(`/analytics/campaign/${campaignId}/sections`),
        ]);
        setData(a.data);
        setSections(s.data);
      } catch (err) {
        console.error("Failed to load analytics:", err);
      }
    })();
  }, [campaignId]);

  const scrollData = data
    ? [25, 50, 75, 90].map((d) => ({ name: `${d}%`, value: data.scroll_depth?.[d] || 0 }))
    : [];

  const funnelData = data
    ? [1, 2, 3, 4].map((step) => ({ name: `מדריך ${step}`, value: data.funnel_steps?.[step] || 0 }))
    : [];

  return (
    <AdminLayout title="אנליטיקה">
      <div className="bg-white rounded-2xl p-4 border border-stone-200 mb-6 flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
        <div className="md:max-w-sm w-full">
          <Select value={campaignId} onValueChange={setCampaignId}>
            <SelectTrigger className="h-11" data-testid="analytics-campaign-select">
              <SelectValue placeholder="בחר קמפיין" />
            </SelectTrigger>
            <SelectContent>
              {campaigns.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.brand_name} (/{c.slug})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!data ? (
        <p className="text-stone-500">בחר קמפיין כדי לראות נתונים.</p>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Kpi label="צפיות" value={data.views} />
            <Kpi label="לידים" value={data.leads} />
            <Kpi label="יחס המרה" value={`${data.conversion_rate}%`} accent />
            <Kpi label="קליקים אפיליאייט" value={data.affiliate_clicks} />
          </div>

          {/* Consent metrics */}
          <ConsentMetrics data={data} />

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Scroll Depth */}
            <Panel title="עומק גלילה">
              <div className="h-64 min-h-[200px] min-w-0">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={scrollData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
                    <XAxis dataKey="name" tick={{ fill: "#44403c" }} />
                    <YAxis tick={{ fill: "#44403c" }} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#d97706" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>

            {/* Funnel Steps */}
            <Panel title="התקדמות במשפך">
              <div className="h-64 min-h-[200px] min-w-0">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={funnelData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" horizontal={false} />
                    <XAxis type="number" tick={{ fill: "#44403c" }} />
                    <YAxis dataKey="name" type="category" tick={{ fill: "#44403c" }} />
                    <Tooltip />
                    <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                      {funnelData.map((d, i) => (
                        <Cell key={d.name} fill={FUNNEL_COLORS[i]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>
          </div>

          {/* Event counts */}
          <Panel title="התפלגות אירועים" className="mt-6">
            {Object.keys(data.event_counts || {}).length === 0 ? (
              <p className="text-stone-500 text-sm">אין אירועים עדיין.</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(data.event_counts).map(([k, v]) => (
                  <div key={k} className="bg-stone-50 rounded-xl p-3" data-testid={`event-${k}`}>
                    <div className="text-xs text-stone-500 mb-1 truncate">{k}</div>
                    <div className="text-xl font-[800]">{v}</div>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          {/* Section Dwell Heatmap */}
          <Panel title="חום קריאה לפי מקטעים (Section Dwell Heatmap)" className="mt-6">
            {sections.length === 0 ? (
              <p className="text-stone-500 text-sm">אין נתוני קריאה עדיין.</p>
            ) : (
              <SectionHeatmap rows={sections} />
            )}
          </Panel>
        </>
      )}
    </AdminLayout>
  );
}

function SectionHeatmap({ rows }) {
  const maxAvg = Math.max(...rows.map((r) => r.avg_ms), 1);
  const sectionLabels = {
    hero: "Hero / טופס",
    pain: "בולטי כאב",
    explanation: "מקטע הסבר",
    questionnaire: "מקטע שאלון",
    stats: "סטטיסטיקות",
    bonus: "בונוס וואטסאפ",
  };
  return (
    <div className="space-y-3" data-testid="section-heatmap">
      {rows.map((r) => {
        const pct = Math.round((r.avg_ms / maxAvg) * 100);
        const seconds = (r.avg_ms / 1000).toFixed(1);
        return (
          <div key={r.section} className="bg-stone-50 rounded-xl p-3" data-testid={`heatmap-row-${r.section}`}>
            <div className="flex items-center justify-between text-sm mb-2">
              <div className="font-bold">{sectionLabels[r.section] || r.section}</div>
              <div className="flex gap-3 text-xs text-stone-500">
                <span><strong className="text-brand-charcoal">{seconds}s</strong> ממוצע</span>
                <span>{r.unique_viewers} צופים</span>
              </div>
            </div>
            <div className="h-3 rounded-full bg-stone-200 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-l from-brand-amber to-amber-300 transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-stone-200">
      <div className="text-xs text-stone-500 mb-1">{label}</div>
      <div className={`text-3xl font-[800] ${accent ? "text-brand-amber" : ""}`}>{value}</div>
    </div>
  );
}

function ConsentMetrics({ data }) {
  const accept = data.event_counts?.cookie_consent_accept_all || 0;
  const custom = data.event_counts?.cookie_consent_custom_save || 0;
  const totalDecisions = accept + custom;
  const acceptanceRate = totalDecisions > 0 ? Math.round((accept / totalDecisions) * 100) : 0;
  return (
    <div className="bg-white rounded-2xl p-6 border border-stone-200 mb-6" data-testid="consent-metrics">
      <h3 className="font-[800] mb-1">הסכמת עוגיות (Consent Rate)</h3>
      <p className="text-sm text-stone-500 mb-4">יחס בין משתמשים שלחצו "אישור הכל" לבין כל המחליטים בבאנר</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-stone-50 rounded-xl p-3" data-testid="kpi-consent-accept">
          <div className="text-xs text-stone-500">לחצו "אישור הכל"</div>
          <div className="text-2xl font-[800] text-emerald-600">{accept}</div>
        </div>
        <div className="bg-stone-50 rounded-xl p-3" data-testid="kpi-consent-custom">
          <div className="text-xs text-stone-500">בחרו הגדרות מותאמות</div>
          <div className="text-2xl font-[800]">{custom}</div>
        </div>
        <div className="bg-stone-50 rounded-xl p-3" data-testid="kpi-consent-total">
          <div className="text-xs text-stone-500">סה"כ החלטות</div>
          <div className="text-2xl font-[800]">{totalDecisions}</div>
        </div>
        <div className="bg-amber-50 rounded-xl p-3 border border-amber-200" data-testid="kpi-consent-rate">
          <div className="text-xs text-stone-700 font-bold">Acceptance Rate</div>
          <div className="text-3xl font-[800] text-brand-amber">{acceptanceRate}%</div>
        </div>
      </div>
    </div>
  );
}

function Panel({ title, children, className = "" }) {
  return (
    <div className={`bg-white rounded-2xl p-6 border border-stone-200 ${className}`}>
      <h3 className="font-[800] mb-4">{title}</h3>
      {children}
    </div>
  );
}
